import { z } from 'zod'

export const FRAME_RATES = [
  { label: '23.976', value: 24000 / 1001 },
  { label: '24', value: 24 },
  { label: '25', value: 25 },
  { label: '29.97', value: 30000 / 1001 },
  { label: '30', value: 30 },
  { label: '50', value: 50 },
  { label: '59.94', value: 60000 / 1001 },
  { label: '60', value: 60 },
]

export const fpsOptions = FRAME_RATES.map((fr) => ({
  label: fr.label,
  value: fr.value,
}))

const FPS_MATCH_TOLERANCE = 0.01

const findFrameRate = (val: number) =>
  FRAME_RATES.find(({ value }) => Math.abs(val - value) < FPS_MATCH_TOLERANCE)

/** Normalizes an API fps value to the nearest canonical frame rate value. */
export const findNearestFrameRate = (fps: number): number =>
  findFrameRate(fps)?.value ?? fps

export const fpsSchema = z.coerce.number().transform((val, ctx) => {
  const match = findFrameRate(val)
  if (!match) {
    ctx.addIssue({
      code: z.ZodIssueCode.custom,
      message: `Unrecognized frame rate: ${val}`,
    })
    return z.NEVER
  }
  return match.value
})

export type Framerate = number

/**
 * Returns the friendly label for a frame rate value with a tolerance.
 */
export const getFrameRateLabel = (fps: number): string => {
  const fr = findFrameRate(fps)
  return fr?.label ?? String(fps)
}

export const serializeDimensions = (d: { width: number; height: number }) =>
  `${d.width}x${d.height}`

export const deserializeDimensions = (value: string) => {
  const [width, height] = value.split('x').map(Number)
  return { width, height }
}

// DISCUSSION: Dimensions are set via API. dimensions and dimensionsSchema need to become numbers.
export const dimensions = [
  { width: 1920, height: 1080 },
  { width: 1280, height: 720 },
] as const

const dimensionSchema = z
  .object({
    width: z.number(),
    height: z.number(),
  })
  .or(z.string().transform(deserializeDimensions))

export const sampleRates = [48000, 44100, 22050]
export const sampleRateSchema = z.coerce
  .number()
  .refine((val) => sampleRates.includes(val))

export type SampleRate = 48000 | 44100 | 22050

export const mediaFormatSchema = z.object({
  fps: fpsSchema,
  dimensions: dimensionSchema.refine((dim) =>
    dimensions.some(
      (option) => option.width === dim.width && option.height === dim.height
    )
  ),
  sampleRate: sampleRateSchema,
})

export type MediaFormat = z.infer<typeof mediaFormatSchema>
